/**
 * #2110 — the unread counts come from the server, and the panel tells the
 * server what it is reading.
 *
 * ══ WHY THE COUNT LEFT THE BROWSER ══
 *
 * Until #2110 the chat box counted unread in memory: `onChannelDoc` added one
 * per message that was not its own and not on screen. That count reset to zero
 * on every reload, was different in every tab, and had nothing to do with the
 * number on the phone. cs3-chat #29 keeps a read position per person per
 * channel and answers `GET /api/me/unread` from it, so the web now reads that
 * and never counts for itself — "the same counter on web and phone" (Ed).
 *
 * The server already leaves out a person's own messages, which is what the
 * `lastSenderUid === me.uid` comparison in the panel used to approximate.
 *
 * ══ THE TWO ENDPOINTS ══
 *
 *   GET /api/me/unread                    { total, channels: [{channel_id, unread}] }
 *                                         channels with nothing unread are LEFT OUT
 *   PUT /api/channels/{id}/read           { message_id?: ulid, viewing?: bool }
 *                                         → { channel_id, last_read_message_id,
 *                                             viewing_until, unread }
 *
 * The mark only moves FORWARD on the server (cs3-chat `ReadMark::advance`), so
 * two tabs sending out of order is not a problem and sending a mark the server
 * already has is not an error. `viewing` defaults to TRUE server-side: a mark
 * means "this is on my screen", and `viewing: false` is the explicit "I have
 * stopped looking" that drops the person out of the push exclusion at once
 * rather than when the lease lapses.
 *
 * ⚠ THE LEASE IS 45 SECONDS (`ReadMark::DEFAULT_VIEWING_SECONDS`) and the panel
 * renews it every RENEW_SECONDS while the conversation stays open, so a person
 * reading a quiet channel keeps counting as "looking" for the push.
 *
 * ⚠ A MESSAGE ID THAT IS NOT A MESSAGE OF THAT CHANNEL IS A 404, so the id sent
 * is always one Firestore delivered for the open channel (`newestShownId`), and
 * a value that is not even shaped like a ULID is dropped rather than sent.
 *
 * Everything here takes its collaborators as arguments — the request, the
 * timers — so the timing can run under node with fakes. `chatPanel.vue` cannot
 * be loaded there.
 */

/** cs3-chat `ReadMark::MESSAGE_ID_PATTERN`: a ULID, either case. */
export const MESSAGE_ID_PATTERN = /^[0-7][0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{25}$/;

/** cs3-chat's lease, for reference; the panel never assumes it, it renews under it. */
export const VIEWING_SECONDS = 45;

/**
 * How often an open conversation re-sends `viewing: true`. Comfortably under
 * the lease so a slow request cannot let it lapse mid-conversation, and long
 * enough that an idle open channel costs two requests a minute, not a stream.
 */
export const RENEW_SECONDS = 30;

/**
 * How long the count refresh waits for more triggers before asking. Firestore
 * replays every channel document the moment the listeners attach, and a burst
 * of messages fires the doc listener once each; one request answers all of
 * them. Long enough to coalesce a burst, short enough that a badge follows a
 * message by well under a second.
 */
export const REFRESH_DELAY_MS = 400;

export function isMessageId(value) {
    return typeof value === 'string' && MESSAGE_ID_PATTERN.test(value);
}

/**
 * The id to mark read: the NEWEST message on screen, which is the last one of
 * the oldest-at-the-top list `listenToChannel` delivers. Scans back past
 * anything that is not a real message id (the preview's invented rows, a
 * malformed document) rather than sending it and getting a 422.
 *
 * @return {string|null}
 */
export function newestShownId(messages) {
    const list = Array.isArray(messages) ? messages : [];
    for (let i = list.length - 1; i >= 0; i -= 1) {
        const id = list[i] && list[i].id;
        if (isMessageId(id)) return id;
    }

    return null;
}

/**
 * `GET /api/me/unread`'s body, as `{ total, counts: {channelId: n} }`.
 *
 * Tolerant of a missing or malformed body — the badge reads zero, which is the
 * right answer to "we could not ask" — but never of a negative or fractional
 * count, which would be a server bug rendered as a badge.
 */
export function normaliseUnread(raw) {
    const r = raw || {};
    const counts = {};
    let sum = 0;

    (Array.isArray(r.channels) ? r.channels : []).forEach((row) => {
        if (!row || typeof row.channel_id !== 'string' || row.channel_id === '') return;
        const n = toCount(row.unread);
        if (n <= 0) return;
        counts[row.channel_id] = n;
        sum += n;
    });

    // The server's total is the badge (it covers every readable channel, which
    // is the roster); the sum stands in when the total is missing or absurd.
    const total = toCount(r.total);

    return { total: Number.isFinite(r.total) && total >= sum ? total : sum, counts };
}

/**
 * Write the server's counts onto the roster rows, and say which channels ROSE.
 *
 * Every row is written: a channel the server did not list has nothing unread,
 * and leaving its old number in place is exactly the stale count this issue
 * removes. The rises are what the chime, the flash and the toast key off now
 * that nothing counts locally — a channel whose count went UP since the last
 * answer has a new message somebody else sent.
 *
 * ⚠ THE FIRST ANSWER IS A BASELINE, NOT AN EVENT. Counts carried over from
 * before this page load are unread, and the rows go bold, but nothing chimes
 * for them: `baseline: false` reports no rises. (The same rule `primed` applied
 * to the Firestore documents before #2110.)
 *
 * @param {Array}   channels  the roster rows, mutated in place (Vue keeps identity)
 * @param {object}  unread    from `normaliseUnread`
 * @param {object}  [opts]    `{ baseline: boolean }` — has a previous answer been applied?
 * @return {{ total: number, rose: string[] }}
 */
export function applyUnreadCounts(channels, unread, opts) {
    const u = unread || { total: 0, counts: {} };
    const counts = u.counts || {};
    const baseline = !!(opts && opts.baseline);
    const rose = [];

    (Array.isArray(channels) ? channels : []).forEach((channel) => {
        if (!channel || !channel.id) return;
        const before = toCount(channel.unread);
        const after = toCount(counts[channel.id]);
        channel.unread = after;
        if (baseline && after > before) rose.push(channel.id);
    });

    return { total: toCount(u.total), rose };
}

/**
 * `PUT /api/channels/{id}/read` answered with that channel's count: write it
 * onto the row and move the badge total by the difference, so the badge does
 * not wait for the next refresh to stop counting a conversation that is open.
 *
 * @return {number} the new total
 */
export function applyMarkResult(channels, result, total) {
    const r = result || {};
    const channel = (Array.isArray(channels) ? channels : []).find((c) => c && c.id === r.channel_id);
    if (!channel) return toCount(total);

    const before = toCount(channel.unread);
    const after = toCount(r.unread);
    channel.unread = after;

    return Math.max(0, toCount(total) - before + after);
}

/**
 * Is this channel on the person's screen right now?
 *
 * All of: the panel is showing (in its own window, or docked and expanded — a
 * page whose chat is undocked shows no panel at all), the channel view is up
 * rather than the list, it is THIS channel, and the tab itself is visible.
 *
 * ⚠ `hidden` IS PART OF IT. A conversation left open in a background tab is
 * not being read, and marking it read from there would clear a count the
 * person never saw — and keep them out of the push.
 *
 * (Before #2110 the panel's own check ignored `windowMode`, so the pop-out
 * never counted as looking.)
 */
export function isLooking(state, channelId) {
    const s = state || {};
    if (!channelId || s.openId !== channelId) return false;
    if (s.view !== 'channel') return false;
    if (s.hidden === true) return false;

    const showing = s.windowMode ? true : (!s.undocked && !s.collapsed);

    return !!showing;
}

/**
 * The unread endpoint's failures, as codes. Nothing in the panel SHOWS these —
 * a count that cannot be fetched stays what it was — but the warning in the
 * console should say which it was.
 */
export function unreadFailureCode(status) {
    return {
        401: 'chat_session_expired',
        403: 'not_permitted',
        404: 'chat_channel_gone',
        422: 'chat_mark_rejected',
    }[status] || 'chat_unreachable';
}

/**
 * The read marker: what the panel tells the server, and when.
 *
 *   look(channelId, messageId)  this channel is on screen, read up to here
 *   leave()                     nothing is on screen any more
 *   stop()                      leave, for `beforeDestroy`
 *
 * `look` is idempotent — the panel calls it from every place its inputs can
 * change (open, collapse, tab focus, messages arriving) and only something NEW
 * produces a request: a different channel, or a newer message in the same one.
 * While a channel stays on screen the lease is renewed every `renewMs`.
 *
 * Switching channels sends `viewing: false` for the old one FIRST, so a person
 * reading channel B is not still "looking at" channel A for the push until A's
 * lease runs out.
 *
 * `send(channelId, messageId, viewing)` is the request, and its failures are
 * the caller's to log: the marker never retries, because the next real change
 * sends again anyway.
 */
export function createReadMarker({ send, setInterval: every, clearInterval: stopEvery, renewMs }) {
    const renewAfter = Number.isFinite(renewMs) && renewMs > 0 ? renewMs : RENEW_SECONDS * 1000;
    const tick = every || globalThis.setInterval;
    const untick = stopEvery || globalThis.clearInterval;

    let current = null;    // { channelId, messageId }
    let timer = null;

    function clearTimer() {
        if (timer !== null) { untick(timer); timer = null; }
    }

    function post(channelId, messageId, viewing) {
        try {
            const p = send(channelId, messageId, viewing);
            // A rejected promise here is the caller's to report; the marker's
            // state does not depend on the answer.
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch (e) { /* a synchronous throw is the same as a rejection */ }
    }

    function leave() {
        if (!current) return;
        const was = current;
        current = null;
        clearTimer();
        post(was.channelId, was.messageId, false);
    }

    return {
        look(channelId, messageId) {
            if (!channelId) { leave(); return; }
            const id = isMessageId(messageId) ? messageId.toUpperCase() : null;

            if (current && current.channelId === channelId) {
                // Same channel. Only a NEWER message is worth a request — the
                // server ignores an older one and the renew timer covers the
                // lease. ULIDs sort by time, so string order is message order.
                if (id === null || (current.messageId !== null && id <= current.messageId)) return;
                current = { channelId, messageId: id };
                post(channelId, id, true);

                return;
            }

            leave();
            current = { channelId, messageId: id };
            post(channelId, id, true);
            timer = tick(() => {
                if (current) post(current.channelId, current.messageId, true);
            }, renewAfter);
        },
        leave,
        stop() { leave(); },
        /** Test seam. */
        current() { return current ? { ...current } : null; },
    };
}

/**
 * The count refresh, coalesced.
 *
 *   request()   ask soon — many calls in `delayMs` become one request
 *   stop()      for `beforeDestroy`
 *
 * ⚠ A REQUEST MADE WHILE ONE IS IN FLIGHT RUNS AGAIN AFTERWARDS, rather than
 * being dropped: the in-flight answer was computed before whatever just
 * happened (a mark, a message) and would otherwise be the last word.
 */
export function createRefresher({ run, delayMs, setTimeout: later, clearTimeout: unlater }) {
    const delay = Number.isFinite(delayMs) && delayMs >= 0 ? delayMs : REFRESH_DELAY_MS;
    const schedule = later || globalThis.setTimeout;
    const unschedule = unlater || globalThis.clearTimeout;

    let timer = null;
    let inFlight = false;
    let again = false;
    let stopped = false;

    async function fire() {
        timer = null;
        if (stopped) return;
        if (inFlight) { again = true; return; }

        inFlight = true;
        try {
            await run();
        } catch (e) {
            // The caller's `run` reports its own failures; a throw here must
            // not wedge the refresher with `inFlight` stuck on.
        } finally {
            inFlight = false;
        }
        if (again && !stopped) {
            again = false;
            fire();
        }
    }

    return {
        request() {
            if (stopped) return;
            if (inFlight) { again = true; return; }
            if (timer !== null) unschedule(timer);
            timer = schedule(fire, delay);
        },
        stop() {
            stopped = true;
            if (timer !== null) { unschedule(timer); timer = null; }
            again = false;
        },
        /** Test seam. */
        pending() { return timer !== null || inFlight || again; },
    };
}

function toCount(v) {
    const n = Number(v);

    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}
